import { useEffect, useRef, type ReactNode } from "react";
import { AnimatePresence, motion } from "motion/react";
import styles from "./Modal.module.css";

interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
  /** Accessible label for the close icon button. */
  closeLabel?: string;
}

// Selector matching elements that are reachable via Tab. We exclude
// elements that are explicitly removed from the tab order, disabled,
// or `inert`. Used by the focus trap to wrap Tab/Shift+Tab.
const FOCUSABLE_SELECTOR = [
  'a[href]',
  'area[href]',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'button:not([disabled])',
  'iframe',
  'object',
  'embed',
  '[contenteditable]:not([contenteditable="false"])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

function getFocusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)).filter(
    (el) => !el.hasAttribute('disabled') && el.tabIndex !== -1,
  );
}

/**
 * Minimal accessible modal:
 * - Escape closes (browser-level keydown listener while open).
 * - Click on backdrop closes; click inside the panel does not.
 * - Focus moves to the first focusable element on open (the panel itself
 *   if nothing inside is focusable).
 * - Tab cycles within the panel (focus trap); Shift+Tab on the first
 *   focusable wraps to the last, and Tab on the last wraps to the first.
 * - On close, focus is restored to the element that triggered the open.
 * - role="dialog" + aria-modal + aria-labelledby on the title.
 */
export function Modal({
  open,
  onClose,
  title,
  children,
  footer,
  wide,
  closeLabel = "Close dialog",
}: ModalProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLElement | null>(null);
  const titleId = useRef(`sf-modal-${Math.random().toString(36).slice(2, 9)}`);

  useEffect(() => {
    if (!open) return;

    // Remember which element opened the modal so we can restore focus
    // when the modal closes. Falls back to null when no element is active
    // (e.g. focus was on <body>).
    const previouslyFocused =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    triggerRef.current = previouslyFocused;

    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const root = panelRef.current;
      if (!root) return;
      const focusables = getFocusable(root);
      if (focusables.length === 0) {
        // Nothing focusable inside — keep focus on the panel itself so
        // Tab/Shift+Tab don't escape the dialog.
        e.preventDefault();
        root.focus();
        return;
      }
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !root.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (active === last || !root.contains(active)) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKey);
    // Focus first focusable in panel (fall back to the panel itself so
    // the dialog can still receive keyboard events when it contains no
    // focusable children — e.g. a pure informational modal).
    const t = window.setTimeout(() => {
      const root = panelRef.current;
      if (!root) return;
      const focusables = getFocusable(root);
      if (focusables.length > 0) {
        focusables[0]!.focus();
      } else {
        root.focus();
      }
    }, 30);
    return () => {
      document.removeEventListener("keydown", onKey);
      window.clearTimeout(t);
      // Restore focus to the trigger when the modal unmounts/closes.
      // Guard against the trigger having been removed from the DOM.
      const trigger = triggerRef.current;
      if (trigger && document.contains(trigger)) {
        trigger.focus();
      }
      triggerRef.current = null;
    };
  }, [open, onClose]);

  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          className={styles.backdrop}
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onClose();
          }}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.12, ease: [0.2, 0, 0, 1] }}
        >
          <motion.div
            ref={panelRef}
            tabIndex={-1}
            className={wide ? `${styles.panel} ${styles.panelWide}` : styles.panel}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId.current}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.18, ease: [0.2, 0, 0, 1] }}
          >
            <div className={styles.header}>
              <h3 id={titleId.current}>{title}</h3>
              <button
                type="button"
                className={styles.close}
                onClick={onClose}
                aria-label={closeLabel}
              >
                &times;
              </button>
            </div>
            <div className={styles.body}>{children}</div>
            {footer ? <div className={styles.footer}>{footer}</div> : null}
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
